import React from 'react';
import { Outlet } from 'react-router-dom';
import * as Sentry from '@sentry/react';

function ErrorFallback({ section, error, onRetry }) {
  const params = new URLSearchParams({ from: 'error' });
  if (section) params.set('section', section);
  params.set('url', window.location.pathname);
  if (error?.message) params.set('errorMsg', error.message);
  const reportUrl = `/mis-tickets?${params.toString()}`;

  return (
    <div
      role="alert"
      className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg p-10 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-950">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-amber-500 dark:text-amber-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-1">
          ¡Ups! Algo no salió bien
        </h1>
        {section && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mb-4 uppercase tracking-wide">
            Sección: {section}
          </p>
        )}

        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed mb-8">
          Esta sección tuvo un problema.
          <br />
          No te preocupés, tus datos están seguros.
        </p>

        <div className="flex flex-col gap-3">
          <button
            onClick={onRetry}
            type="button"
            className="inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-semibold text-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
          >
            Recargar esta sección
          </button>

          <a
            href="/"
            className="inline-flex items-center justify-center gap-1 rounded-lg px-6 py-3 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-sm transition-colors duration-200"
          >
            Ir al inicio →
          </a>
        </div>

        <a
          href={reportUrl}
          className="mt-6 inline-block text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 underline underline-offset-2 transition-colors duration-200"
        >
          ¿El problema persiste? Reportar un problema
        </a>
      </div>
    </div>
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error(
      `[ErrorBoundary${this.props.section ? ` — ${this.props.section}` : ''}]`,
      error,
      info.componentStack
    );
    // No reportamos errores de chunk stale post-deploy: lazyWithRetry ya recarga
    // la página y son ruido en Sentry (falsos positivos del flujo de deploy).
    const message = error?.message || '';
    const isChunkLoadError =
      /Failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /Importing a module script failed/i.test(message) ||
      /is not a valid JavaScript MIME type/i.test(message); // Safari iOS: chunk stale devuelve HTML 404
    if (!isChunkLoadError) {
      Sentry.captureException(error, {
        extra: { section: this.props.section, componentStack: info.componentStack },
      });
    }
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          section={this.props.section}
          error={this.state.error}
          onRetry={this.handleRetry}
        />
      );
    }
    // children cuando se usa como wrapper explícito; Outlet cuando es layout route de React Router
    return this.props.children ?? <Outlet />;
  }
}
